import { useId, useMemo } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import {
  MRT_ColumnDef,
  MRT_TableContainer,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import {
  AssignmentSnapshot,
  PlannerSlot,
  slotTime,
} from "@/components/appointmentRequest/assignmentPlanner/assignmentPlan";
import styles from "@/components/appointmentRequest/assignmentPlanner/planner.module.css";

export interface PlannerChange {
  subject_id: string;
  previous_slot_id: string | null;
  slot_id: string | null;
}

interface ChangeRow {
  id: string;
  employee: string;
  previous?: PlannerSlot;
  target?: PlannerSlot;
  kind: "new" | "move" | "cancel" | "clear";
}

function renderSlot(
  slot: PlannerSlot | undefined,
  sharedDay: string | undefined,
  emptyLabel: string,
) {
  if (!slot) return <span className={styles.changeEmpty}>{emptyLabel}</span>;
  const start = dayjs(slot.start_date);
  const end = dayjs(slot.end_date);
  return (
    <span className={styles.changeSlot}>
      {!sharedDay && (
        <time dateTime={start.format("YYYY-MM-DD")}>
          {start.format("DD.MM.YYYY")}
        </time>
      )}
      {start.isSame(end, "day") ? (
        <span>{slotTime(slot)}</span>
      ) : (
        <span>
          {start.format("HH:mm")} – {end.format("DD.MM.YYYY, HH:mm")}
        </span>
      )}
    </span>
  );
}

export default function PlannerChangeTable({
  snapshot,
  changes,
}: {
  snapshot: AssignmentSnapshot;
  changes: PlannerChange[];
}) {
  const { t, i18n } = useTranslation();
  const summaryId = useId();
  const rows = useMemo<ChangeRow[]>(() => {
    const employees = new Map(
      snapshot.employees.map((employee) => [employee.id, employee]),
    );
    const slots = new Map(snapshot.slots.map((slot) => [slot.id, slot]));
    return changes.map((change) => {
      const employee = employees.get(change.subject_id);
      let kind: ChangeRow["kind"] = change.previous_slot_id ? "move" : "new";
      if (!change.slot_id) kind = employee?.assignment ? "cancel" : "clear";
      return {
        id: change.subject_id,
        employee: employee?.name ?? t("planner.employee"),
        previous: change.previous_slot_id
          ? slots.get(change.previous_slot_id)
          : undefined,
        target: change.slot_id ? slots.get(change.slot_id) : undefined,
        kind,
      };
    });
  }, [snapshot, changes, t]);
  const days = new Set(
    rows.flatMap((row) =>
      [row.previous, row.target].flatMap((slot) =>
        slot
          ? [
              dayjs(slot.start_date).format("YYYY-MM-DD"),
              dayjs(slot.end_date).format("YYYY-MM-DD"),
            ]
          : [],
      ),
    ),
  );
  const sharedDay = days.size === 1 ? [...days][0] : undefined;
  const columns = useMemo<MRT_ColumnDef<ChangeRow>[]>(
    () => [
      {
        accessorKey: "employee",
        header: t("planner.employee"),
        size: 180,
        Cell: ({ row }) => (
          <span className={styles.changeEmployee}>
            <strong>{row.original.employee}</strong>
            <small
              className={
                row.original.kind === "cancel"
                  ? styles.changeCancellation
                  : styles.changeKind
              }
            >
              {t(
                {
                  new: "planner.new_assignment",
                  move: "planner.moved_assignment",
                  cancel: "planner.canceled_assignment",
                  clear: "planner.clear",
                }[row.original.kind],
              )}
            </small>
          </span>
        ),
      },
      {
        id: "previous",
        header: t("planner.previous"),
        size: 150,
        Cell: ({ row }) =>
          renderSlot(row.original.previous, sharedDay, t("planner.unassigned")),
      },
      {
        id: "target",
        header: t("planner.next"),
        size: 150,
        Cell: ({ row }) =>
          renderSlot(row.original.target, sharedDay, t("planner.unassigned")),
      },
    ],
    [sharedDay, t],
  );
  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization: i18n.language.startsWith("en")
      ? MRT_Localization_EN
      : MRT_Localization_DE,
    getRowId: (row) => row.id,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableSorting: false,
    enablePagination: false,
    enableStickyHeader: true,
    initialState: { density: "compact" },
    muiTableProps: {
      "aria-label": t("planner.review"),
      "aria-describedby": summaryId,
      sx: { minWidth: 440 },
    },
    muiTableBodyCellProps: {
      sx: {
        verticalAlign: "top",
        whiteSpace: "normal",
        overflowWrap: "anywhere",
      },
    },
    muiTableContainerProps: {
      tabIndex: 0,
      role: "region",
      "aria-label": t("planner.review"),
      sx: {
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "min(50vh, 28rem)",
        border: "1px solid #e0e6eb",
        borderRadius: "6px",
      },
    },
  });
  return (
    <div className={styles.changeReview}>
      <div id={summaryId} className={styles.changeSummary}>
        {sharedDay && (
          <strong>
            <time dateTime={sharedDay}>
              {dayjs(sharedDay).format("DD.MM.YYYY")}
            </time>
          </strong>
        )}
        <span>{t("planner.change_count", { count: rows.length })}</span>
      </div>
      <MRT_TableContainer table={table} />
    </div>
  );
}
